import get from 'lodash.get';
import merge from 'lodash.merge';
import Big from 'big.js';

import env from '#shared/config/env.js';
import getContentGroup from '#shared/util/getContentGroup.js';
import isObject from '#shared/util/isObject.js';
import isString from '#shared/util/isString.js';
import { decode as decodeAswparam } from '#shared/util/aswparam.js';
import Price from '#shared/util/Price.js';
import isAlphaIdInAswparam from '#shared/util/isAlphaIdInAswparam.js';

import { getRequestQuery, getRequestQueryAll } from '#backend/util/request.js';

import { offerSchema } from './offer.js';
import { productSchema } from './product.js';
import { countrySchema } from './country.js';

/**
 * @typedef {import('express').Request} ExpressRequest
 * @typedef {import('express').Response} ExpressResponse
 */

/**
 * Calculate installment value (should be replaced by eStore values in the future)
 * @param {number} total Order total amount
 * @param {number} installmentNumber Number of installments
 * @param {Price} priceFormatter
 * @returns {object}
 */
const calculateInstallment = (total, installmentNumber, priceFormatter) => {
	const installmentValue = new Big(total).div(installmentNumber);
	return priceFormatter.formatPrice(installmentValue);
};

/**
 * Create order price object with the expected structure
 * @param {object} cart Object with raw cart data from eStore API
 * @param {object[]} products Array of products in cart formatted by productSchema
 * @returns {object}
 */
const createProviderPriceObject = (cart, products) => {
	if (!products.length || !get(cart, 'result.price')) {
		return {};
	}
	// @todo GEN - Temporary hack to get formatted order pricing
	const [firstProduct] = products || [];
	const priceFormatter = firstProduct ? new Price(firstProduct.price.street.formatted) : null;
	const totalPrice = cart.result.price.total;
	const totalPriceFormatted = priceFormatter.formatPrice(totalPrice);
	const installmentsString = cart.result.attributes.installments;
	let installments = {};
	// Format installments from NLOK's data string
	if (installmentsString) {
		installments = installmentsString // Installments (received format: '1:99.00,2:49.50,3:33.00')
			.split(',') // Split it into the array (e.g.: ['1:99.00', '2:49.50', '3:33.00'])
			.map((i) => i.split(':').map(Number)) // Split each item into the array and convert to number (e.g.: [[1, 99.00], [2, 49.50], [3, 33.00]])
			.map(([numberOfInstallments, priceUnformatted]) => [numberOfInstallments, priceFormatter.formatPrice(priceUnformatted)]);
	} else {
		// Fallback in case we don't get installments in the response
		installments = [
			[1, totalPriceFormatted],
			[2, calculateInstallment(totalPrice, 2, priceFormatter)],
			[3, calculateInstallment(totalPrice, 3, priceFormatter)]
		];
	}
	return {
		street: priceFormatter.formatPrice(cart.result.price.subTotal), // @todo GEN - Missing value
		list: priceFormatter.formatPrice(0), // @todo GEN - Missing value
		discount: {
			absolute: priceFormatter.formatPrice(0), // @todo GEN - Missing value
			percentage: {
				formatted: '0%', // @todo GEN - Missing value
				unformatted: 0 // @todo GEN - Missing value
			}
		},
		tax: cart.result.tax ? priceFormatter.formatPrice(cart.result.tax.totalTax) : priceFormatter.formatPrice(0),
		total: totalPriceFormatted,
		installments
	};
};

/**
 * Set entity code
 * @param {string} countryCode Country code e.g. (US)
 * @returns {object}
 */
const setEntityCode = (countryCode) => {
	let entityCode = null;
	let entityCodeInvoice = null;
	// Set entityCode once the countryCode is available
	if (countryCode) {
		for (const [merchantId, countryList] of Object.entries(env.merchantMap)) {
			if (countryList.includes(countryCode)) {
				entityCode = merchantId;
			}
		}
		entityCode = entityCode || 'symantec_emea';
		// Set entityCodeInvoice as a workaround for invoice merchant info localization strings
		for (const [merchantLocalizationKey, countryList] of Object.entries(env.merchantAddressMap)) {
			if (countryList.includes(countryCode)) {
				entityCodeInvoice = merchantLocalizationKey;
			}
		}
	}
	return {
		entityCode,
		entityCodeInvoice
	};
};

/**
 * Order schema
 * @param {object} data
 * @param {object} data.cart Object with raw cart data from eStore API
 * @param {object} data.payment Object returned from payment schema
 * @param {object} data.sessionStore Session store instance
 * @param {object} data.regionConfig Object with region configuration
 * @param {ExpressRequest} [data.request] Express request object (available only on server during cart open)
 * @param {ExpressResponse} [data.response] Express response object (available only on server during cart open)
 * @returns {object}
 */
export const orderSchema = ({ cart, payment, sessionStore, regionConfig, request, response }) => {
	const pricingResponse = sessionStore?.store.pricingResponse;
	const cartParameters = sessionStore?.store.cartParameters;
	const aswparamString = cartParameters?.aswparam || getRequestQuery(request, 'aswparam', '');
	const aswparamValue = {
		string: aswparamString,
		object: decodeAswparam(aswparamString)
	};
	const afbDataString = cartParameters?.afbData || getRequestQuery(request, 'afbData', '');
	const afbDataValue = {
		string: afbDataString,
		object: decodeAswparam(afbDataString)
	};
	const customIDs = cartParameters?.customIDs ?? getRequestQueryAll(request, 'customID');
	const countryCode = get(cart, 'result.context.countryCode');
	const aidFromQuery = getRequestQuery(request, 'aid');
	const sidFromQuery = getRequestQuery(request, 'sid');
	const shopperFromQuery = getRequestQuery(request, 'shopper');
	const isAuthenticated = isString(cartParameters?.providerAccountId || aidFromQuery || sidFromQuery || shopperFromQuery, 1);
	const isGuest = !isAuthenticated;
	const impressions = [];
	let flow = 'guest';
	const productsArray = get(cart, 'result.items', []);
	const productsCount = productsArray.length;
	const products = productsArray.map((item, index) => productSchema({
		cart,
		payment,
		index,
		item,
		sessionStore
	}));
	const contentGroups = products.map(({ contentGroup }) => contentGroup);
	const contentGroup = getContentGroup(contentGroups);
	const hasDiscount = products.some((product) => product.price.discount.absolute.unformatted > 0);
	// Notification text
	const hasDisclaimer = products.some((product) => product.disclaimer);
	const disclaimer = products.map(({ disclaimer }) => disclaimer).join('<br/><br/>');
	// Find out if there is a trial product in the order
	const trialProductFromRequest = (cartParameters?.products ?? getRequestQueryAll(request, 'product')).some((foreignId) => foreignId.includes('.tr.'));
	const trialProduct = products.find((product) => product.isTrial);
	const hasTrialProduct = productsCount ? isObject(trialProduct) : trialProductFromRequest;
	const hasBusinessProduct = contentGroup === 'Business';
	const hasManualRenewalProduct = products.some((product) => product.variant === 'MR');
	const hasBrowserProduct = products.some((product) => product.isBrowser);
	const trialPeriod = trialProduct ? trialProduct.trialPeriod : 0;
	// Create price objects for the provider and for the trial products
	const providerPriceObject = createProviderPriceObject(cart, products);
	const conversionPriceObject = {
		conversionPrice: trialProduct ? trialProduct.conversionPrice : null
	};
	// Find out if phone number field should be displayed
	const displayPhoneNumber = products.find((product) => product.displayPhoneNumber) && regionConfig.variables.displayPhoneNumber === 'attribute' || regionConfig.variables.displayPhoneNumber === 'always' || customIDs.includes('PHONESHOW');
	// Disable email edit option (Authenticated cart or SMB flow - afbData)
	const hasLockedEmail = isAuthenticated || isString(afbDataValue.object.email, 1) && hasBusinessProduct;
	// Set correct order campaign according the products in cart
	const [firstCouponInCart] = products.filter((product) => product.coupon !== 'noDiscount');
	const campaign = firstCouponInCart ? firstCouponInCart.coupon : '';
	const campaignMarker = cartParameters?.campaignMarker || getRequestQuery(request, 'campaignMarker', '');
	if (isAuthenticated) {
		flow = 'authenticated';
	}
	if (hasTrialProduct) {
		flow = 'PAT';
	}
	if (cartParameters?.orderSource === 'OMS') {
		flow = 'OMS';
	}
	// Add impressions to the order response
	const offerProductType = customIDs.includes('UPON') ? 'upsell' : 'crossSell';
	const offerProduct = (isGuest || customIDs.includes('CART-4550')) && !hasTrialProduct && products.length === 1 ? offerSchema({
		cart,
		payment,
		sessionStore,
		index: products.length,
		item: pricingResponse[offerProductType],
		offerType: offerProductType
	}) : null;
	const offerProductFoundInAswparam = offerProduct && isAlphaIdInAswparam(offerProduct.alphaIds, aswparamValue.object);
	if (offerProduct && !offerProductFoundInAswparam) {
		impressions.push(offerProduct);
	}
	const { entityCode, entityCodeInvoice } = setEntityCode(countryCode);
	const locale = cartParameters?.locale || response.locals.locale;
	const countryList = countrySchema({
		locale,
		countries: env.countries
	});
	// Secure identity & Sontiq configuration
	const hasSontiqProduct = products.some((product) => product.category && product.category.array.includes('Sontiq'));
	const hasInsuranceProduct = products.some((product) => product.category && product.category.array.includes('Insurance'));
	// Determine if it's Brazilian flow
	const isBrazilianFlow = regionConfig.variables.useBrazilianFlow;
	// T&Cs checkbox
	const hasTermsCheckbox = regionConfig.variables.hasTermsCheckbox;
	// Has prices within the terms and conditions
	const hasPricesInTerms = regionConfig.variables.hasPricesInTerms;
	// Sidebar year note
	const hasYearNote = regionConfig.variables.hasYearNote;
	// Determine if it's OMS flow
	const isOmsFlow = flow === 'OMS';
	// Return order data in unified format
	return {
		aswparam: aswparamValue,
		afbData: afbDataValue,
		campaign,
		campaignMarker,
		contentGroup,
		customIDs,
		isTest: Boolean(cartParameters?.isTest),
		isDebug: Boolean(cartParameters?.isDebug),
		isDeveloper: Boolean(cartParameters?.isDeveloper),
		isGuest,
		isAuthenticated,
		isBrazilianFlow,
		isOmsFlow,
		stateList: isBrazilianFlow ? env.zipMap.br.states : [],
		hasLockedEmail,
		hasDisclaimer,
		disclaimer,
		displayPhoneNumber,
		displayAddressField: regionConfig.variables.displayAddressField,
		disableCountrySelector: regionConfig.variables.disableCountrySelector,
		flow,
		orderState: cart.statusMessage,
		createAccount: regionConfig.variables.createAccount,
		orderDate: new Date(),
		currency: get(cart, 'result.context.currency'),
		productsCount,
		id: cart.orderNumber,
		guid: get(cart, 'result.cartGuid', ''),
		price: merge(providerPriceObject, conversionPriceObject),
		products,
		hasDiscount,
		hasTrialProduct,
		hasSontiqProduct,
		hasInsuranceProduct,
		hasBusinessProduct,
		hasManualRenewalProduct,
		hasBrowserProduct,
		hasTermsCheckbox,
		hasPricesInTerms,
		hasYearNote,
		hasQrPayment: regionConfig.variables.hasQrPayment,
		trialPeriod,
		impressions,
		entityCode,
		entityCodeInvoice: entityCodeInvoice || entityCode,
		country: countryCode,
		countryList,
		attributes: cartParameters?.orderAttributes ?? {}
	};
};
